import { cloneElement } from "react";

import Icons from "assets";
import { Box } from "ui/box/box";
import { IconButton } from "ui/icon-button/icon-button";
import { Popper } from "ui/popper/popper";
import { Grow } from "ui/grow/grow";
import { mergeSx } from "ui/merge-sx";

import { useActionsMenu } from "ui/actions-menu/use-actions-menu";
import { ActionsMenuProvider } from "ui/actions-menu/actions-menu-context";

import { getTransformOrigin } from "ui/actions-menu/actions-menu.helpers";
import { MenuPanel, WRAPPER_SX, POPPER_SX } from "ui/actions-menu/actions-menu.styles";

import { type ActionsMenuProps } from "ui/actions-menu/actions-menu.types";

export const ActionsMenu = ({
  itemName,
  children,
  open,
  onOpen,
  onClose,
  size,
  offsetLeft,
  offsetTop,
  placement,
  sx,
  menuSx,
  trigger,
  menuId,
  ...rest
}: ActionsMenuProps) => {
  const {
    menuId: resolvedMenuId,
    menuLabel,
    menuRefCallback,
    anchorEl,
    placement: requestedPlacement,
    popperModifiers,
    triggerSlotProps,
    openedByKeyboard,
    handleListKeyDown,
  } = useActionsMenu({
    itemName,
    open,
    onOpen,
    onClose,
    offsetLeft,
    offsetTop,
    placement,
    menuId,
    triggerProps: trigger?.props,
  });

  return (
    <Box {...rest} sx={mergeSx(WRAPPER_SX, sx)}>
      {trigger ? (
        cloneElement(trigger, triggerSlotProps)
      ) : (
        <IconButton variant="text" size={size} {...triggerSlotProps}>
          <Icons.More />
        </IconButton>
      )}

      <Popper
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement={requestedPlacement}
        modifiers={popperModifiers}
        role="presentation"
        transition
        sx={POPPER_SX}>
        {({ TransitionProps, placement: paintedPlacement }) => (
          <Grow
            {...TransitionProps}
            timeout="auto"
            style={{ transformOrigin: getTransformOrigin(paintedPlacement) }}>
            <MenuPanel
              id={resolvedMenuId}
              ref={menuRefCallback}
              autoFocusItem={openedByKeyboard}
              onKeyDown={handleListKeyDown}
              aria-label={menuLabel}
              sx={menuSx}>
              <ActionsMenuProvider onClose={onClose}>{children}</ActionsMenuProvider>
            </MenuPanel>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};
